import { elevationCss } from '@creditornot/cb-ingredients';
import { MenuActionItem, MenuItemGroup } from '@creditornot/cb-menu';
import R from 'ramda';
import React, { forwardRef, useImperativeHandle, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { formatMinutes, parseMinutes } from './utils';

type Props = {
  className?: string;
  searchString: string;
  onSearchStringChange: (inputValue: string) => void;
  onSelectTime: (value: number) => void;
};

export type TimeMenuElement = {
  next: () => void;
  previous: () => void;
};

const MINUTE_GAP = 15;
const DAY_DURATION_MIN = 24 * 60;

const Root = styled.div`
  ${elevationCss(8)}
  padding: 8px 16px;
  border-radius: 8px;
  overflow-y: scroll;
  outline: none;
`;

const ListItem = styled(MenuActionItem).attrs({ variant: 'black' })`
  &:not(:active) {
    /* Disable transitions to remove jerkiness when navigating with keyboard and container scrolls to follow */
    transition: none;
  }
`;

const TimeMenu = (
  { className, searchString, onSearchStringChange, onSelectTime }: Props,
  ref: React.Ref<TimeMenuElement>,
) => {
  const menuRef = useRef<HTMLDivElement | null>(null);
  const closestListItemRef = useRef<HTMLDivElement | null>(null);
  const [searchValue, setSearchValue] = useState(parseMinutes(searchString) || 0);

  useLayoutEffect(() => {
    const newValue = parseMinutes(searchString);
    if (newValue !== null) {
      setSearchValue(newValue);
    }
  }, [searchString]);

  useLayoutEffect(() => {
    if (!menuRef.current || !closestListItemRef.current) {
      return;
    }
    const menuRect = menuRef.current.getBoundingClientRect();
    const itemRect = closestListItemRef.current.getBoundingClientRect();
    menuRef.current.scrollTo(
      0,
      menuRef.current.scrollTop + itemRect.top + itemRect.height / 2 - (menuRect.top + menuRect.height / 2),
    );
  }, [searchValue]);

  useImperativeHandle(ref, () => ({
    previous: () => {
      onSearchStringChange(
        formatMinutes(((Math.ceil(searchValue / MINUTE_GAP) - 1) * MINUTE_GAP + DAY_DURATION_MIN) % DAY_DURATION_MIN),
      );
    },
    next: () => {
      onSearchStringChange(formatMinutes(((Math.floor(searchValue / MINUTE_GAP) + 1) * MINUTE_GAP) % DAY_DURATION_MIN));
    },
  }));

  return (
    <Root className={className} ref={menuRef} tabIndex={-1}>
      <MenuItemGroup>
        {R.range(0, Math.floor(DAY_DURATION_MIN / MINUTE_GAP))
          .map(index => index * MINUTE_GAP)
          .map(minutes => (
            <ListItem
              // Disable tabbing because user likely expects the focus to move to the next
              // input when tabbing instead of having to tab through every item on this list.
              tabIndex={-1}
              key={minutes}
              onClick={() => {
                onSelectTime(minutes);
              }}
              selected={minutes === searchValue}
            >
              <div
                ref={
                  Math.floor(minutes / MINUTE_GAP) === Math.floor(searchValue / MINUTE_GAP) ? closestListItemRef : null
                }
              >
                {formatMinutes(minutes)}
              </div>
            </ListItem>
          ))}
      </MenuItemGroup>
    </Root>
  );
};

export default forwardRef<TimeMenuElement, Props>(TimeMenu);
